import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 5000, suffix: '+', label: '成功案例' },
  { value: 300, suffix: '+', label: '覆盖城市' },
  { value: 15, suffix: '年', label: '从业经验' },
  { value: 98, suffix: '%', label: '客户满意度' },
];

const useCountUp = (target: number, duration = 1800, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatItem = ({ value, suffix, label, started }: { value: number; suffix: string; label: string; started: boolean }) => {
  const count = useCountUp(value, 1800, started);
  return (
    <div className="text-center px-4">
      <div className="text-2xl md:text-3xl font-black text-[#c9a84c] leading-none">
        {count}{suffix}
      </div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
};

const HeroSection = () => {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsStarted, setStatsStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative min-h-[520px] md:min-h-[640px] flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/9c09df6cbf5c79a5d11c6139e15d92d9.jpg')" }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1209]/95 via-[#0f1a14]/88 to-[#1a2a1a]/92"></div>

      {/* Animated radial glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#c9a84c]/5 blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] rounded-full bg-[#c9a84c]/4 blur-2xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }}></div>

      {/* Diagonal grid lines */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(201,168,76,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.4) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      ></div>

      {/* Bottom gold shimmer line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/70 to-transparent"></div>

      <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#c9a84c]/10 border border-[#c9a84c]/30 rounded-full px-4 py-1.5 mb-6"
          style={{ animation: 'fadeInDown 0.6s ease both' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] inline-block animate-pulse"></span>
          <span className="text-[#c9a84c] text-xs font-semibold tracking-widest uppercase">Professional Investigation</span>
        </div>

        {/* Title */}
        <h2
          className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight"
          style={{ animation: 'fadeInUp 0.7s ease 0.1s both' }}
        >
          专业侦探
          <span className="relative inline-block mx-2">
            <span className="text-[#c9a84c]">调查服务</span>
            <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent"></span>
          </span>
        </h2>

        {/* Subtitle */}
        <h3
          className="text-sm md:text-base text-white/60 font-medium mb-6 tracking-wide"
          style={{ animation: 'fadeInUp 0.7s ease 0.2s both' }}
        >
          婚外情调查 &nbsp;·&nbsp; 小三调查 &nbsp;·&nbsp; 寻人找人 &nbsp;·&nbsp; 资产调查 &nbsp;·&nbsp; 商业调查
        </h3>

        <p
          className="text-white/50 text-sm mb-8 max-w-xl mx-auto leading-relaxed"
          style={{ animation: 'fadeInUp 0.7s ease 0.3s both' }}
        >
          猎鹰侦探事务所提供全国范围内的专业调查服务，经验丰富的调查团队，严格保密，高效可靠
        </p>

        {/* CTA */}
        <div style={{ animation: 'fadeInUp 0.7s ease 0.4s both' }}>
          <a
            href="#contact"
            className="relative inline-block overflow-hidden bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f1a14] font-black px-10 py-3.5 rounded-md text-base transition-colors cursor-pointer whitespace-nowrap group"
          >
            <span className="relative z-10">免费咨询调查方案</span>
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"></span>
          </a>
        </div>

        {/* Stats */}
        <div
          ref={statsRef}
          className="mt-12 pt-8 border-t border-[#c9a84c]/15 grid grid-cols-2 md:grid-cols-4 gap-4"
          style={{ animation: 'fadeInUp 0.7s ease 0.5s both' }}
        >
          {stats.map((s) => (
            <StatItem key={s.label} {...s} started={statsStarted} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
